import { z } from 'zod';

const externalIdentifier = z.string().trim().max(255, 'Must be 255 characters or fewer');

export const DELIVERY_CHANNELS = ['link', 'call'] as const;
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];

// Twilio outbound calling only supports US numbers, so accept a plain 10-digit
// US number (with or without a leading +1 / 1 and any formatting) and normalize
// to E.164 (+1XXXXXXXXXX). Returns null when it isn't a valid US number.
export function normalizeUsPhone(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  // NANP: 10 digits, area code + exchange each start 2-9.
  if (!/^[2-9]\d{9}$/.test(local)) return null;
  return `+1${local}`;
}

export const createInterviewFormSchema = z
  .object({
    interviewTypeVersionId: z.string().min(1, 'Interview type is required'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    externalUserId: externalIdentifier,
    externalSource: externalIdentifier,
    // Delivery channel the user picked: "link" just creates the invite; "call"
    // also dials the candidate immediately.
    channel: z.enum(DELIVERY_CHANNELS),
    phoneNumber: z.string().trim(),
    // Values for the selected template's declared dynamic variables. Required
    // ones are enforced per-field (the schema can't know the template's shape).
    dynamicVariables: z.record(z.string(), z.string()),
  })
  .superRefine((values, ctx) => {
    const hasUser = values.externalUserId.length > 0;
    const hasSource = values.externalSource.length > 0;
    if (hasUser !== hasSource) {
      ctx.addIssue({
        code: 'custom',
        message: 'Provide both external user ID and external source, or leave both blank.',
        path: hasUser ? ['externalSource'] : ['externalUserId'],
      });
    }
    // A valid US phone is required only when "Call now" is selected.
    if (values.channel === 'call' && !normalizeUsPhone(values.phoneNumber)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a valid US phone number, e.g. (555) 123-4567',
        path: ['phoneNumber'],
      });
    }
  });

export type CreateInterviewFormValues = z.infer<typeof createInterviewFormSchema>;
